"use client";

import { createContext, useContext, useMemo, useState } from "react";

/**
 * Coordinates the top nav so only one dropdown is open at a time. Each dropdown
 * registers under a stable id; opening one immediately closes any sibling,
 * which is what makes hovering from one trigger to the next feel instant
 * instead of leaving two panels stacked on top of each other.
 */
const NavBarContext = createContext(null);

export function NavBarProvider({ children }) {
  const [activeMenu, setActiveMenu] = useState(null);
  const value = useMemo(() => ({ activeMenu, setActiveMenu }), [activeMenu]);
  return (
    <NavBarContext.Provider value={value}>{children}</NavBarContext.Provider>
  );
}

export function useNavBar(menuId) {
  const ctx = useContext(NavBarContext);
  // Fall back to local state if a dropdown is ever rendered outside a provider
  // (e.g. in isolation), so it still opens and closes on its own.
  const [localOpen, setLocalOpen] = useState(null);
  const activeMenu = ctx ? ctx.activeMenu : localOpen;
  const setActiveMenu = ctx ? ctx.setActiveMenu : setLocalOpen;

  const isOpen = activeMenu === menuId;
  const setIsOpen = (next) => {
    const shouldOpen = typeof next === "function" ? next(isOpen) : next;
    if (shouldOpen) setActiveMenu(menuId);
    else setActiveMenu((current) => (current === menuId ? null : current));
  };
  return { isOpen, setIsOpen };
}
